import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DEFAULT_PARTNER_ORG_ID } from "@/constants/system";

export interface Partner {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  partner_type: 'Affiliate' | 'Associate' | 'Reseller';
  status: 'Signed' | 'Not-Signed' | 'Informal';
  agreement_signed: boolean;
  profile_image?: string;
  commission_rate?: number;
  total_earned?: number;
  last_payout_date?: string;
  deals_won?: number;
  revenue_generated?: number;
  conversion_rate?: number;
  partner_tier?: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  goal_revenue?: number;
  goal_deals?: number;
  notes?: string;
  territory?: string;
  specialization?: string;
  user_id?: string;
  portal_access_enabled?: boolean;
  invitation_sent_at?: string;
  invitation_accepted_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePartnerData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  partner_type: 'Affiliate' | 'Associate' | 'Reseller';
  status: 'Signed' | 'Not-Signed' | 'Informal';
  agreement_signed: boolean;
  profile_image?: string;
  commission_rate?: number;
  user_id?: string; // For existing user mode
  mode?: 'new' | 'existing'; // Creation mode
}

export const usePartners = () => {
  return useQuery({
    queryKey: ["partners"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('crm_partners')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching partners:", error);
        throw error;
      }
      return data as Partner[];
    }
  });
};

export const usePartnerList = () => {
  return useQuery({
    queryKey: ['partner-list'],
    queryFn: async () => {
      console.log("Fetching partner list...");
      const { data, error } = await (supabase as any)
        .from('crm_partners')
        .select('id, first_name, last_name, email')
        .order('first_name', { ascending: true });
      
      if (error) {
        console.error("Error fetching partner list:", error);
        throw error;
      }
      
      console.log("Fetched partner list:", data);
      return data || [];
    }
  });
};

export const useCreatePartner = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreatePartnerData) => {
      console.log("Creating partner with data:", data);
      console.log(`Partner will be linked to main Snapshot Live organization: ${DEFAULT_PARTNER_ORG_ID}`);
      
      const isExistingUserMode = data.mode === 'existing' && data.user_id;
      
      // Clean up undefined values and empty strings
      const cleanedData: Record<string, any> = {};
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'mode' && value !== undefined && value !== "" && value !== null) {
          cleanedData[key] = value;
        }
      });
      
      console.log("Cleaned partner data:", cleanedData);
      console.log("Existing user mode:", isExistingUserMode);
      
      // Create the partner record
      const { data: result, error } = await (supabase as any)
        .from('crm_partners')
        .insert(cleanedData)
        .select()
        .single();
      
      if (error) {
        console.error("Insert Error:", error);
        throw new Error(error.message || "Failed to create partner");
      }
      
      console.log(`Partner created and linked to main organization (${DEFAULT_PARTNER_ORG_ID}):`, result);
      
      if (isExistingUserMode) {
        // Partner access is now an add-on capability: the user keeps their
        // existing organisation role and gains a partner record. No second
        // role row is created, so role resolution stays safe.
        console.log("Linking existing user to partner record:", result.user_id);
        
        // Enable portal access and set acceptance date for existing users
        const { error: portalUpdateError } = await supabase
          .from('crm_partners')
          .update({
            portal_access_enabled: true,
            invitation_accepted_at: new Date().toISOString()
          })
          .eq('id', result.id);

        if (portalUpdateError) {
          console.warn("Warning: Failed to enable portal access:", portalUpdateError);
        }
        
        console.log("Partner record linked and portal access enabled for existing user:", result.user_id);
      } else {
        // Create corresponding profile entry for new partner (invitation flow)
        console.log("Creating corresponding profile for partner:", result.id);
        const profileData = {
          id: crypto.randomUUID(),
          first_name: result.first_name,
          last_name: result.last_name,
          email: result.email,
          phone: result.phone || undefined,
          contact_type: 'Partner',
          external_owner_id: result.id,
          organization_id: DEFAULT_PARTNER_ORG_ID,
          intended_system_role: 'partner' as const,
          relationship_status: 'Partner'
        };
        
        const { data: profileResult, error: profileError } = await supabase
          .from('profiles')
          .insert(profileData)
          .select()
          .single();
        
        if (profileError) {
          console.error("Profile creation error:", profileError);
          await supabase.from('crm_partners').delete().eq('id', result.id);
          throw new Error(`Failed to create partner profile: ${profileError.message}`);
        }
        
        console.log("Partner profile created successfully:", profileResult.id);
      }
      
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      queryClient.invalidateQueries({ queryKey: ["available-users"] });
      toast({
        title: "Success",
        description: "Partner created successfully",
      });
    },
    onError: (error) => {
      console.error("Create partner error:", error);
      toast({
        title: "Error",
        description: `Failed to create partner: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useUpdatePartner = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreatePartnerData> }) => {
      const { data: result, error } = await (supabase as any)
        .from('crm_partners')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error("Update Error:", error);
        throw new Error(error.message || "Failed to update partner");
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast({
        title: "Success",
        description: "Partner updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update partner: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useDeletePartner = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('crm_partners')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error("Delete Error:", error);
        throw new Error(error.message || "Failed to delete partner");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast({
        title: "Success",
        description: "Partner deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error", 
        description: `Failed to delete partner: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};
